import { Injectable } from '@angular/core';
import {combineLatest, Observable, withLatestFrom} from 'rxjs';
import {filter, first, map} from 'rxjs/operators';
import {AppState} from '../store/store';
import {Store} from '@ngrx/store';
import {ComponentType} from '@angular/cdk/overlay';
import {WidgetTextComponent} from '../../reports/dashboard-report/dashboard-report/widgets/widget-text/widget-text.component';
import {WidgetChartComponent} from '../../reports/dashboard-report/dashboard-report/widgets/widget-chart/widget-chart.component';
import {WidgetTableComponent} from '../../reports/dashboard-report/dashboard-report/widgets/widget-table/widget-table.component';
import {WidgetTotalsComponent} from '../../reports/dashboard-report/dashboard-report/widgets/widget-totals/widget-totals.component';
import {DimensionComponentObject, DimensionComponentObjects} from '../classes/dimension-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../classes/metric-component-object';
import {WidgetUtils} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/widget-utils';
import {DashboardWidget} from '../store/report/report-dashboard';
import {
  ConfigureWidgetTextComponent
} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/configure-widget-text/configure-widget-text.component';
import {
  ConfigureWidgetPieChartComponent
} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/configure-widget-pie-chart/configure-widget-pie-chart.component';
import {
  ConfigureWidgetAxisChartComponent
} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/configure-widget-axis-chart/configure-widget-axis-chart.component';
import {
  ConfigureWidgetTableComponent
} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/configure-widget-table/configure-widget-table.component';
import {
  ConfigureWidgetTotalsComponent
} from '../../reports/dashboard-report/dashboard-report/add-widget-dialog/configure-widget-totals/configure-widget-totals.component';
import {
  selectDimensionComponentObjects,
  selectDimensionsLoaded,
  selectMetricComponentObjects,
  selectMetricsLoaded
} from '../store/selectors';

@Injectable({
  providedIn: 'root'
})
export class ReportDashboardService {

  constructor(
    private readonly _store: Store<AppState>
  ) { }

  public savedDashboardAdapter(dashboard: Array<any>): Observable<Array<DashboardWidget>> {
    return combineLatest([
      this._store.select(selectMetricsLoaded),
      this._store.select(selectDimensionsLoaded)
    ]).pipe(
      filter(([dimensions, metrics]: [boolean, boolean]): boolean => dimensions && metrics),
      first(),
      withLatestFrom(
        this._store.select(selectMetricComponentObjects),
        this._store.select(selectDimensionComponentObjects)
      ),
      map(([_, metrics, dimensions]: [[boolean, boolean], MetricComponentObjects, DimensionComponentObjects]): Array<DashboardWidget> => {
        for (const widget of dashboard) {
          widget.config.widget = this.getWidgetComponent(widget.config.widget);

          if (widget.config.data) {
            for (const item of widget.config.data) {
              this.getConfigParams(item, dimensions, metrics);
            }
          } else {
            this.getConfigParams(widget, dimensions, metrics);
          }
        }

        return dashboard;
      })
    );
  }

  public getWidgetComponent(widget: string): ComponentType<WidgetTextComponent | WidgetChartComponent | WidgetTableComponent | WidgetTotalsComponent> {
    switch (widget) {
      case 'widgetTextComponent':
        return WidgetTextComponent;
      case 'widgetChartComponent':
        return WidgetChartComponent;
      case 'widgetTableComponent':
        return WidgetTableComponent;
      case 'widgetTotalsComponent':
        return WidgetTotalsComponent;
    }
  }

  public getConfigureComponent(widget: DashboardWidget): ComponentType<any> {
    switch (widget.config.widget) {
      case WidgetTextComponent:
        return ConfigureWidgetTextComponent;
      case WidgetChartComponent:
        return widget.config.type === 'pie' && ConfigureWidgetPieChartComponent || ConfigureWidgetAxisChartComponent;
      case WidgetTableComponent:
        return ConfigureWidgetTableComponent;
      case WidgetTotalsComponent:
        return ConfigureWidgetTotalsComponent;
    }
  }

  public getConfigParams(widget: DashboardWidget, dimensions: Array<DimensionComponentObject>, metrics: Array<MetricComponentObject>): void {
    widget.params = {dimensions: [], metrics: [], filters: []};

    for (const slug of widget.config?.dimensions || widget.dimensions || []) {
      const dimension: DimensionComponentObject = dimensions.find((dim: DimensionComponentObject): boolean => dim.payload.attributes.slug === slug);

      if (dimension) {
        widget.params.dimensions.push(dimension);
      }
    }

    widget.params.filters = WidgetUtils.getFilterDimensions(widget.config?.filters || widget.filters, dimensions);

    for (const slug of widget.config?.metrics || widget.metrics || []) {
      const metric: MetricComponentObject = metrics.find((met: MetricComponentObject): boolean => met.payload.attributes.slug === slug);

      if (metric) {
        widget.params.metrics.push(metric);
      }
    }
  }

  public getWidgetText(widget: ComponentType<any>): string {
    switch (widget) {
      case WidgetTextComponent:
        return 'widgetTextComponent';
      case WidgetChartComponent:
        return 'widgetChartComponent';
      case WidgetTableComponent:
        return 'widgetTableComponent';
      case WidgetTotalsComponent:
        return 'widgetTotalsComponent';
    }
  }
}
